import { apiClient } from '../../apiHelper';
import { useCreate, useDelete, useEdit, useSingleFetch } from '../react-query';
import orgUnitsQueryKeys from './org-units-query-keys';

export type OrgUnitType =
  | 'ORG'
  | 'REGION'
  | 'PROVINCE'
  | 'DIVISION'
  | 'FACILITY';

export interface OrgUnit {
  id: string;
  parent_id: string | null;
  type: OrgUnitType;
  name: string;
  is_phi_scoped: boolean;
  // The API serializes this as `meta` (alias); `metadata` kept for safety.
  meta?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface OrgUnitNode extends OrgUnit {
  children: OrgUnitNode[];
}

export interface OrgUnitCreate {
  type: OrgUnitType;
  name: string;
  parent_id?: string | null;
  is_phi_scoped?: boolean;
  metadata?: Record<string, unknown>;
}

export interface OrgUnitUpdate {
  name?: string;
  is_phi_scoped?: boolean;
  metadata?: Record<string, unknown>;
}

export function useOrgTree(config = {}) {
  const getTreeFn = async (): Promise<OrgUnitNode | null> => {
    const r: any = await apiClient.get('org-units/tree');
    return r && typeof r === 'object' && 'id' in r ? (r as OrgUnitNode) : null;
  };
  return useSingleFetch(getTreeFn, orgUnitsQueryKeys.tree(), 'tree', config);
}

export function useCreateOrgUnit(
  onSuccess?: (response: any, data: OrgUnitCreate) => void,
  onError?: (error: any, data: OrgUnitCreate) => void
) {
  const createFn = async (data: OrgUnitCreate): Promise<OrgUnit> => {
    const response = await apiClient.post('org-units', data);
    return (response?.data ?? response) as OrgUnit;
  };
  return useCreate(
    createFn,
    orgUnitsQueryKeys.all,
    'Error while creating org unit',
    'Org unit created',
    onSuccess,
    onError
  );
}

export function useUpdateOrgUnit(
  id: string,
  onSuccess?: (response: any, data: OrgUnitUpdate) => void,
  onError?: (error: any, data: OrgUnitUpdate) => void
) {
  const updateFn = async (data: OrgUnitUpdate): Promise<OrgUnit> => {
    const response = await apiClient.patch(`org-units/${id}`, data);
    return (response?.data ?? response) as OrgUnit;
  };
  return useEdit(
    updateFn,
    orgUnitsQueryKeys.all,
    'Error while updating org unit',
    'Org unit updated',
    onSuccess,
    onError
  );
}

export function useDeleteOrgUnit(
  onSuccess?: (response: any, id: string) => void,
  onError?: (error: any, id: string) => void
) {
  const deleteFn = async (id: string): Promise<void> => {
    await apiClient.delete(`org-units/${id}`);
  };
  return useDelete(
    deleteFn,
    orgUnitsQueryKeys.all,
    'Error while deleting org unit',
    'Org unit deleted'
  );
}

export interface OrgTemplate {
  key: string;
  label: string;
  description: string;
  levels: OrgUnitType[];
}

export function useOrgTemplates(config = {}) {
  const fn = async (): Promise<{ shapes: OrgTemplate[]; node_types: OrgUnitType[] }> => {
    const r: any = await apiClient.get('org-units/templates');
    return (r?.data ?? r) as { shapes: OrgTemplate[]; node_types: OrgUnitType[] };
  };
  return useSingleFetch(fn, ['org-templates'], 'templates', config);
}

export function useApplyOrgTemplate(
  onSuccess?: (response: any, shape: string) => void,
  onError?: (error: any, shape: string) => void
) {
  const fn = async (shape: string): Promise<any> =>
    apiClient.post('org-units/apply-template', { shape });
  return useEdit(
    fn,
    orgUnitsQueryKeys.all,
    'Error while applying the structure template',
    'Organization structure updated',
    onSuccess,
    onError
  );
}
